import { memo, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { relativeTime } from '../lib/relativeTime'

interface MeshGrid {
  handle: string
  slug: string
  title: string
  description: string | null
  tags: string[]
  rows: number
  version: number
  updated_at: string
  cols?: number
}

interface ApiRow {
  handle: string
  slug: string
  title: string
  description: string | null
  tags: string | string[] | null
  rows: number
  version: number
  updated_at: string
  columns: string | string[] | null
}

const EXAMPLE_QUERIES = [
  'world cup fixtures',
  'agent frameworks 2026',
  'MCP servers',
  'AI coding IDEs',
  'top scorers',
]

type SortKey = 'relevance' | 'rows' | 'recent'
const SORT_OPTIONS: ReadonlyArray<readonly [SortKey, string]> = [
  ['relevance', 'relevance'],
  ['rows',      'most rows'],
  ['recent',    'recent'],
]

const VIOLET = '#B79DFF'

function parseJsonField<T>(v: string | T | null, fallback: T): T {
  if (v == null) return fallback
  if (typeof v !== 'string') return v
  try { return JSON.parse(v) as T } catch { return fallback }
}

function normalise(rows: ApiRow[]): MeshGrid[] {
  return rows.map(r => ({
    handle: r.handle,
    slug: r.slug,
    title: r.title,
    description: r.description,
    tags: parseJsonField<string[]>(r.tags ?? null, []),
    rows: r.rows,
    version: r.version,
    updated_at: r.updated_at,
    cols: parseJsonField<string[]>(r.columns ?? null, []).length || undefined,
  }))
}

function fmtRows(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K'
  return n.toLocaleString()
}

export default function Showcase() {
  const [params, setParams] = useSearchParams()
  const initialQuery = params.get('q') ?? ''

  const [grids, setGrids] = useState<MeshGrid[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState(initialQuery)
  const [submitted, setSubmitted] = useState(initialQuery)
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set())
  const [sort, setSort] = useState<SortKey>('relevance')

  useEffect(() => {
    document.title = submitted
      ? `${submitted} · Showcase | Instadash`
      : 'Showcase · Curated Public Grids | Instadash'
  }, [submitted])

  useEffect(() => {
    let cancelled = false
    fetch('/api/mesh/grids?handle=showcase&limit=100')
      .then(r => r.json())
      .then((body: { grids: ApiRow[] }) => {
        if (cancelled) return
        setGrids(normalise(body.grids ?? []))
        setLoading(false)
      })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  function runSearch(q: string) {
    setSubmitted(q)
    setParams(q ? { q } : {})
  }

  function toggleTag(t: string) {
    setActiveTags(prev => {
      const next = new Set(prev)
      next.has(t) ? next.delete(t) : next.add(t)
      return next
    })
  }

  function clearAll() {
    setSubmitted('')
    setQuery('')
    setActiveTags(new Set())
    setParams({})
  }

  const trendingTags = useMemo(() => {
    const counts = new Map<string, number>()
    for (const g of grids) for (const t of g.tags) counts.set(t, (counts.get(t) ?? 0) + 1)
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 14)
      .map(([tag, count]) => ({ tag, count }))
  }, [grids])

  const results = useMemo(() => {
    const qLower = submitted.toLowerCase().trim()
    const qTokens = qLower.split(/\s+/).filter(Boolean)
    let scored = grids.map(g => {
      let score = 0
      if (qTokens.length === 0) {
        score = 0.5
      } else {
        const hay = [g.title, g.slug, g.description ?? '', g.tags.join(' ')].join(' ').toLowerCase()
        for (const t of qTokens) {
          if (g.title.toLowerCase().includes(t)) score += 0.4
          if (g.slug.toLowerCase().includes(t))  score += 0.35
          if ((g.description ?? '').toLowerCase().includes(t)) score += 0.25
          if (g.tags.some(tag => tag.toLowerCase().includes(t))) score += 0.3
          if (hay.includes(t)) score += 0.05
        }
      }
      return { ...g, score: Math.min(score, 0.99) }
    })
    if (qTokens.length > 0) scored = scored.filter(g => g.score > 0.05)
    if (activeTags.size > 0) scored = scored.filter(g => [...activeTags].every(t => g.tags.includes(t)))
    if (sort === 'relevance') scored.sort((a, b) => b.score - a.score || +new Date(b.updated_at) - +new Date(a.updated_at))
    else if (sort === 'rows')   scored.sort((a, b) => b.rows - a.rows)
    else if (sort === 'recent') scored.sort((a, b) => +new Date(b.updated_at) - +new Date(a.updated_at))
    return scored
  }, [grids, submitted, activeTags, sort])

  const totalRows = useMemo(() => grids.reduce((acc, g) => acc + g.rows, 0), [grids])

  return (
    <div data-screen-label="showcase" style={{ minHeight: '60vh' }}>
      {/* Hero */}
      <section style={{ padding: '56px 0 32px', borderBottom: '1px solid var(--line-soft)' }}>
        <div className="container-wide">
          <div className="row" style={{ alignItems: 'baseline', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
            <span className="mono" style={{ fontSize: 11, color: VIOLET, letterSpacing: '0.14em', textTransform: 'uppercase' }}>◇ showcase</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>/ curated public grids</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>·</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--lime)' }}>
              ● {grids.length} grid{grids.length === 1 ? '' : 's'}
              {totalRows > 0 && <> · {fmtRows(totalRows)} rows</>}
            </span>
          </div>

          <h1 className="h1" style={{ maxWidth: 880, marginBottom: 14 }}>
            Public grids. <span style={{ color: VIOLET }}>Live, queryable, embeddable.</span>
          </h1>
          <p className="lede" style={{ marginBottom: 28, maxWidth: 640 }}>
            Every grid is a single push from an agent. JSON API, markdown, llms.txt,
            and embeddable widget on every URL. Search to filter, or click a grid to open.
          </p>

          {/* Search */}
          <form onSubmit={(e) => { e.preventDefault(); runSearch(query) }} className="row" style={{ gap: 10, marginBottom: 18, alignItems: 'stretch' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <span className="mono" style={{
                position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                color: VIOLET, fontSize: 14, pointerEvents: 'none',
              }}>⌕</span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='Try "world cup", "agent frameworks", "MCP servers"…'
                className="mono"
                style={{
                  width: '100%', padding: '14px 110px 14px 40px', fontSize: 14,
                  background: 'var(--bg-1)', border: '1px solid var(--line)',
                  borderRadius: 8, color: 'var(--fg)', outline: 'none',
                  fontFamily: 'var(--mono)',
                }}
                onFocus={(e) => (e.target.style.borderColor = VIOLET)}
                onBlur={(e) => (e.target.style.borderColor = 'var(--line)')}
              />
              <span className="mono" style={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--fg-3)', fontSize: 10, padding: '3px 7px',
                border: '1px solid var(--line)', borderRadius: 3, background: 'var(--bg)',
              }}>↵</span>
            </div>
            <button type="submit"
              className="btn btn-mono"
              style={{
                background: VIOLET, borderColor: VIOLET, color: '#1a1530',
                fontSize: 13, padding: '0 22px', fontWeight: 600,
              }}>
              search →
            </button>
          </form>

          {/* Example queries */}
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginRight: 6 }}>try</span>
            {EXAMPLE_QUERIES.map(q => (
              <button key={q} onClick={() => { setQuery(q); runSearch(q) }}
                className="mono"
                style={{
                  background: 'transparent', border: '1px solid var(--line-soft)',
                  borderRadius: 4, padding: '5px 10px', fontSize: 11,
                  color: 'var(--fg-2)', cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = VIOLET; e.currentTarget.style.color = VIOLET }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line-soft)'; e.currentTarget.style.color = 'var(--fg-2)' }}
              >
                ↳ {q}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <div className="container-wide" style={{ paddingTop: 24 }}>
        <div className="resp-grid-4" style={{
          gap: 1,
          background: 'var(--line-soft)', border: '1px solid var(--line-soft)',
          borderRadius: 8, overflow: 'hidden',
        }}>
          {[
            { l: 'curated grids', v: grids.length.toLocaleString(),    sub: 'showcase handle' },
            { l: 'rows indexed',  v: fmtRows(totalRows),                sub: 'across showcase', accent: true },
            { l: 'tags',          v: trendingTags.length.toString(),    sub: 'unique facets' },
            { l: 'updates',       v: 'live',                            sub: 'auto-refreshed via mesh' },
          ].map(s => (
            <div key={s.l} style={{ background: 'var(--bg-1)', padding: 16 }}>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>{s.l}</div>
              <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: s.accent ? VIOLET : 'var(--fg)' }}>{s.v}</div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 4 }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Main: filter rail + results */}
      <div className="container-wide showcase-main">
        {/* Filter rail */}
        <aside className="showcase-rail">
          <FilterSection label="tags">
            <div className="col gap-4">
              {trendingTags.length === 0 && <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>—</span>}
              {trendingTags.map(t => (
                <button key={t.tag} onClick={() => toggleTag(t.tag)} className="mono"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 10px', fontSize: 11,
                    background: activeTags.has(t.tag) ? 'rgba(183,157,255,0.12)' : 'transparent',
                    border: '1px solid ' + (activeTags.has(t.tag) ? 'rgba(183,157,255,0.4)' : 'var(--line-soft)'),
                    borderRadius: 4,
                    color: activeTags.has(t.tag) ? VIOLET : 'var(--fg-2)',
                    cursor: 'pointer', textAlign: 'left',
                  }}>
                  <span className="row gap-4">
                    <span style={{ color: activeTags.has(t.tag) ? VIOLET : 'var(--fg-3)' }}>#</span>
                    {t.tag}
                  </span>
                  <span style={{ color: 'var(--fg-3)', fontSize: 10 }}>{t.count}</span>
                </button>
              ))}
            </div>
          </FilterSection>

          <div style={{
            padding: 12, marginTop: 16,
            background: 'var(--bg-1)', border: '1px solid var(--line-soft)', borderRadius: 6,
          }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>push your own</div>
            <pre className="mono" style={{ fontSize: 10, color: 'var(--lime)', lineHeight: 1.6, margin: 0 }}>
{`echo '[…]' | npx instadash \\
  push --name my-grid`}
            </pre>
            <Link to="/docs" className="mono"
              style={{
                display: 'inline-block', marginTop: 10, color: VIOLET, fontSize: 10,
              }}>
              docs: quickstart →
            </Link>
          </div>
        </aside>

        {/* Results column */}
        <div style={{ minWidth: 0 }}>
          {/* Results header */}
          <div className="row" style={{ marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
            <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
              <span className="mono" style={{ fontSize: 11, color: 'var(--lime)' }}>● {results.length} grid{results.length === 1 ? '' : 's'}</span>
              <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>· {loading ? 'loading…' : 'ready'}</span>
              {submitted && (
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-2)' }}>
                  · query: <span style={{ color: VIOLET }}>"{submitted}"</span>
                  <button
                    onClick={clearAll}
                    style={{ marginLeft: 6, background: 'transparent', border: 'none', color: 'var(--fg-3)', cursor: 'pointer', fontFamily: 'var(--mono)' }}>
                    ×
                  </button>
                </span>
              )}
              {activeTags.size > 0 && (
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-2)' }}>
                  · #{[...activeTags].join(' #')}
                  <button
                    onClick={() => setActiveTags(new Set())}
                    style={{ marginLeft: 6, background: 'transparent', border: 'none', color: 'var(--fg-3)', cursor: 'pointer', fontFamily: 'var(--mono)' }}>
                    clear
                  </button>
                </span>
              )}
            </div>
            <span className="spacer"></span>
            <div className="row gap-4" style={{ background: 'var(--bg-1)', border: '1px solid var(--line-soft)', borderRadius: 6, padding: 3 }}>
              {SORT_OPTIONS.map(([k, l]) => (
                <button key={k} onClick={() => setSort(k)} className="mono"
                  style={{
                    padding: '4px 10px', fontSize: 11, borderRadius: 3,
                    background: sort === k ? 'var(--bg-3)' : 'transparent',
                    color: sort === k ? 'var(--fg)' : 'var(--fg-2)',
                    border: 'none', cursor: 'pointer',
                  }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Result list */}
          <div className="col gap-12">
            {loading && <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--fg-3)' }}>Loading curated grids…</div>}
            {!loading && results.length === 0 && (
              <div className="card" style={{ padding: 40, textAlign: 'center' }}>
                <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>0 grids</div>
                <div className="h3" style={{ marginBottom: 8 }}>No grids match.</div>
                <p className="muted" style={{ fontSize: 13, marginBottom: 18 }}>Try a broader query or clear the tag filter.</p>
                <button onClick={clearAll} className="btn btn-ghost btn-mono" style={{ fontSize: 12 }}>
                  clear filters
                </button>
              </div>
            )}
            {!loading && results.map((g, i) => (
              <ResultRow key={`${g.handle}/${g.slug}`} grid={g} idx={i} showScore={!!submitted} />
            ))}
          </div>

          {/* Footer cta */}
          {!loading && results.length > 0 && (
            <div className="card" style={{ marginTop: 32, padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>via mcp</div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Search the mesh from any agent</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  Drop the Instadash MCP server into Claude, Cursor, or your custom agent and call{' '}
                  <code className="mono" style={{ color: 'var(--accent)' }}>instamesh_search(q)</code>.
                </div>
              </div>
              <div className="row gap-8">
                <Link to="/mcp" className="btn btn-ghost btn-mono" style={{ fontSize: 12 }}>docs: mcp →</Link>
                <a
                  href="/.well-known/mcp.json"
                  className="btn btn-primary btn-mono"
                  style={{ fontSize: 12 }}>
                  view mcp.json
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Result row ────────────────────────────────────────────────────────

const ResultRow = memo(function ResultRow({ grid, idx, showScore }: { grid: MeshGrid & { score?: number }; idx: number; showScore: boolean }) {
  const matchPct = grid.score != null ? Math.round(grid.score * 100) : null
  const matchColor = (grid.score ?? 0) > 0.6 ? 'var(--lime)' : VIOLET

  return (
    <div className="card" style={{ borderColor: 'var(--line-soft)' }}>
      <div className="showcase-card">
        {/* Rank gutter */}
        <div className="showcase-card-rank" style={{
          padding: '18px 0 18px 14px', fontFamily: 'var(--mono)', fontSize: 11,
          color: 'var(--fg-3)', borderRight: '1px solid var(--line-soft)',
        }}>
          {String(idx + 1).padStart(2, '0')}
        </div>

        {/* Main */}
        <div style={{ padding: '16px 18px', minWidth: 0 }}>
          <div className="row" style={{ gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 6 }}>
            <Link to={`/${grid.handle}/${grid.slug}`} className="mono" style={{ fontSize: 15, fontWeight: 600, color: 'var(--accent)' }}>
              {grid.slug}
            </Link>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>by</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-1)' }}>{grid.handle}</span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>· v{grid.version}</span>
          </div>

          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6, letterSpacing: '-0.01em' }}>{grid.title || grid.slug}</div>
          {grid.description && (
            <div className="muted" style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12, maxWidth: '68ch' }}>{grid.description}</div>
          )}

          {/* Tags */}
          {grid.tags.length > 0 && (
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {grid.tags.slice(0, 6).map(t => (
                <span key={t} className="mono" style={{
                  fontSize: 10, padding: '2px 7px', borderRadius: 3,
                  background: 'var(--bg-2)', border: '1px solid var(--line-soft)',
                  color: 'var(--fg-2)',
                }}>#{t}</span>
              ))}
            </div>
          )}
        </div>

        {/* Stats column */}
        <div className="showcase-card-stats" style={{ padding: '16px 16px', borderLeft: '1px solid var(--line-soft)' }}>
          <div className="col gap-4">
            <Stat label="rows"     value={fmtRows(grid.rows)} />
            {grid.cols != null && <Stat label="cols" value={String(grid.cols)} />}
            <Stat label="version"  value={'v' + grid.version} />
            <Stat label="updated"  value={relativeTime(grid.updated_at)} />
          </div>
        </div>

        {/* Match / open column */}
        <div className="showcase-card-cta" style={{
          padding: '16px 16px', borderLeft: '1px solid var(--line-soft)',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 12,
          background: 'var(--bg-1)',
        }}>
          {showScore && matchPct != null ? (
            <div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>match</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: matchColor, fontFamily: 'var(--mono)', letterSpacing: '-0.02em' }}>{matchPct}%</div>
              <div style={{ height: 4, background: 'var(--bg-3)', borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
                <div style={{ width: matchPct + '%', height: '100%', background: matchColor }}></div>
              </div>
            </div>
          ) : (
            <div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>formats</div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--fg-1)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span>JSON</span>
                <span>llms.md</span>
                <span>llms.txt</span>
              </div>
            </div>
          )}
          <Link
            to={`/${grid.handle}/${grid.slug}`}
            className="btn btn-mono"
            style={{ fontSize: 11, padding: '6px 10px', background: VIOLET, borderColor: VIOLET, color: '#1a1530', justifyContent: 'center' }}>
            open →
          </Link>
        </div>
      </div>
    </div>
  )
})

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{label}</span>
      <span className="mono" style={{ fontSize: 11, color: 'var(--fg-1)', fontWeight: 500 }}>{value}</span>
    </div>
  )
}

function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
        ─ {label}
      </div>
      {children}
    </div>
  )
}
